import DeleteIcon from '@mui/icons-material/Delete';
import { Button, Divider, IconButton, Skeleton, Stack, TextField, Typography } from '@mui/material';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useCreateOrUpdateCropMonitoringResultNote, useDeleteCropMonitoringResultNote } from '/@/hooks/reports';
import { _ } from '/@/locale';
import type { CropMonitoringResultNotes } from '/@/types/crop-monitoring';
import { formatDate } from '/@/utils';

interface CropMonitoring {
    id: number;
}

interface Note {
    id: number;
    content: string;
    editable: boolean;
}

interface FormValues {
    content: string;
    notes: Note[];
}

const CropMonitoringReportNotes = ({
    cropMonitoring,
    cropMonitoringResultNotesQuery,
    isPdf,
}: {
    cropMonitoring: CropMonitoring;
    cropMonitoringResultNotesQuery: CropMonitoringResultNotes;
    isPdf: boolean;
}) => {
    const form = useForm<FormValues>({ defaultValues: { content: '', notes: [] } });
    const { control, watch, setValue, getValues, reset } = form;

    const content = watch('content');

    const { data, isLoading } = cropMonitoringResultNotesQuery;

    const createOrUpdateNote = useCreateOrUpdateCropMonitoringResultNote({
        cropMonitoringId: cropMonitoring?.id,
    });

    const deleteNote = useDeleteCropMonitoringResultNote({
        cropMonitoringId: cropMonitoring?.id,
    });

    useEffect(() => {
        if (data?.results) {
            const formattedNotes = data.results.map((note) => ({
                ...note,
                editable: false,
            }));
            setValue('notes', formattedNotes);
        }
    }, [data, setValue]);

    const handleAddNote = async () => {
        if (!content.trim()) return;
        await createOrUpdateNote.trigger({ content });
        reset({ content: '' });
    };

    const handleEditNote = async (noteId: number, noteContent: string) => {
        await createOrUpdateNote.trigger({ id: noteId, content: noteContent });
    };

    const handleDeleteNote = async (noteId: number) => {
        await deleteNote.trigger({ id: noteId });
    };

    const toggleEditMode = (index: number, note: Note) => {
        const fieldPath = `notes.${index}`;
        const isEditing = getValues(`${fieldPath}.editable`);
        if (isEditing) {
            handleEditNote(note.id, getValues(`${fieldPath}.content`));
        }
        setValue(`${fieldPath}.editable`, !isEditing);
    };

    const renderNote = (note: Note, index: number) => {
        const fieldPath = `notes.${index}`;
        const noteIsEditable = watch(`${fieldPath}.editable`);

        return (
            <Stack key={note.id} px={1} py={1.5} spacing={2} direction='row' alignItems='center'>
                <Stack flex={1}>
                    <Typography variant='subtitle1' fontWeight='bold' color='primary'>
                        {`${[note.createdBy?.firstName, note.createdBy?.lastName].join(' ')} - ${formatDate(new Date(note.created))}`}
                    </Typography>
                    {noteIsEditable ? (
                        <Controller
                            control={control}
                            name={`${fieldPath}.content`}
                            render={({ field }) => (
                                <TextField
                                    fullWidth
                                    multiline
                                    {...field}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            toggleEditMode(index, note);
                                        }
                                    }}
                                />
                            )}
                        />
                    ) : (
                        <Typography variant='body2' color='primary.mui.inactive'>
                            {note.content}
                        </Typography>
                    )}
                </Stack>
                {!isPdf && (
                    <Stack mb='auto !important' ml='auto' direction='row' alignItems='center'>
                        <Typography
                            onClick={() => toggleEditMode(index, note)}
                            color='secondary'
                            sx={{
                                textDecoration: 'underline',
                                cursor: 'pointer',
                            }}
                            variant='body2'
                        >
                            {noteIsEditable ? _('Guardar') : _('Editar')}
                        </Typography>
                        <IconButton size='small' onClick={() => handleDeleteNote(note.id)}>
                            <DeleteIcon color='error' />
                        </IconButton>
                    </Stack>
                )}
            </Stack>
        );
    };

    return (
        <Stack sx={{ border: (t) => `1px solid ${t.palette.divider}`, height: '100%' }}>
            <Stack p={2} mb='auto'>
                <Typography variant='h6' color='primary'>
                    {_('Notas de la campaña')}
                </Typography>
            </Stack>
            <Divider />
            <Stack spacing={3} p={2}>
                <Stack spacing={2}>
                    {isLoading ? (
                        <Skeleton variant='rectangular' width='100%' height='160px' />
                    ) : data?.count === 0 ? (
                        <Stack py={6} px={4} alignItems='center' justifyContent='center'>
                            <Typography variant='subtitle1' fontWeight='bold' color='primary'>
                                {_('Todavía no hay notas en esta campaña.')}
                            </Typography>
                            <Typography variant='body2' color='primary.mui.inactive'>
                                {_(
                                    'Podés usar este espacio para registrar observaciones, aprendizajes o decisiones importantes que quieras tener a mano más adelante.'
                                )}
                            </Typography>
                        </Stack>
                    ) : (
                        data?.results.map(renderNote)
                    )}
                </Stack>
                {!isPdf && (
                    <Stack spacing={1}>
                        <Controller
                            control={control}
                            name='content'
                            render={({ field }) => (
                                <TextField
                                    label={_('Añadir una nota')}
                                    fullWidth
                                    multiline
                                    {...field}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            handleAddNote();
                                        }
                                    }}
                                />
                            )}
                        />
                        <Button variant='text' color='secondary' onClick={handleAddNote}>
                            {_('Añadir Nota')}
                        </Button>
                    </Stack>
                )}
            </Stack>
        </Stack>
    );
};

export default CropMonitoringReportNotes;
